import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Pencil, X } from "lucide-react";

const EMPTY = { name: "", category: "fabric", subcategory: "", description: "", image: "", status: "In Stock", details: "", featured: false };
const CATEGORIES = ["fabric", "kurta", "accessory"];
const STATUSES = ["In Stock", "Available for Custom Tailoring", "Exclusive Archive"];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited or EMPTY for new

  const load = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([api.get("/leads"), api.get("/products")]);
      setLeads(l.data);
      setProducts(p.data);
    } catch {
      toast.error("Failed to load data.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const deleteLead = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((v) => v.filter((x) => x._id !== id));
    toast.success("Lead removed.");
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      if (editing._id) {
        const { _id, id, created_at, ...body } = editing;
        await api.put(`/products/${editing._id}`, body);
        toast.success("Product updated.");
      } else {
        await api.post("/products", editing);
        toast.success("Product added.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    }
  };

  const deleteProduct = async (id) => {
    await api.delete(`/products/${id}`);
    setProducts((v) => v.filter((x) => x._id !== id));
    toast.success("Product deleted.");
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-charcoal/15 bg-cream sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-2xl tracking-tight text-toga">TOGA</span>
            <span className="text-[9px] tracking-[0.3em] uppercase text-charcoal/60 font-bold">Admin Console</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="hidden sm:block text-xs uppercase tracking-[0.15em] text-charcoal/60">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-charcoal hover:text-toga transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        <div className="flex gap-3 mb-10">
          {["leads", "products"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold border transition-colors ${tab === t ? "bg-charcoal text-cream border-charcoal" : "border-charcoal/25 text-charcoal hover:border-toga hover:text-toga"}`}>
              {t} {t === "leads" ? `(${leads.length})` : `(${products.length})`}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div data-testid="leads-panel" className="border border-charcoal/15">
            {leads.length === 0 && <p className="p-10 text-charcoal/50">No inquiries yet.</p>}
            {leads.map((l) => (
              <div key={l._id} data-testid={`lead-row-${l._id}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-6 border-b border-charcoal/10 last:border-0 items-start">
                <div className="md:col-span-3">
                  <p className="font-display uppercase font-bold text-charcoal text-lg">{l.name}</p>
                  <p className="text-sm text-charcoal/60">{l.phone}</p>
                  {l.email && <p className="text-sm text-charcoal/60">{l.email}</p>}
                </div>
                <div className="md:col-span-7">
                  {l.interest && <p className="text-sm"><span className="text-toga uppercase text-xs tracking-wide font-bold">Interest: </span>{l.interest}</p>}
                  {l.product_name && <p className="text-sm text-charcoal/70">Re: {l.product_name}</p>}
                  {l.message && <p className="text-sm text-charcoal/70 mt-1">{l.message}</p>}
                  <p className="text-xs text-charcoal/40 mt-2">{new Date(l.created_at).toLocaleString()}</p>
                </div>
                <div className="md:col-span-2 flex md:justify-end gap-3">
                  <a href={`https://wa.me/${l.phone.replace(/\D/g, "").length >= 10 ? l.phone.replace(/\D/g, "") : ""}`} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-wide font-bold text-charcoal hover:text-toga">Reply</a>
                  <button onClick={() => deleteLead(l._id)} data-testid={`delete-lead-${l._id}`} className="text-charcoal/50 hover:text-toga"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="products-panel">
            <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-product-btn" className="mb-6 inline-flex items-center gap-2 bg-toga text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold hover:bg-charcoal transition-colors">
              <Plus size={16} /> Add Product
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/15 border border-charcoal/15">
              {products.map((p) => (
                <div key={p._id} data-testid={`admin-product-${p._id}`} className="bg-cream p-4">
                  <div className="aspect-video overflow-hidden mb-3"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
                  <span className="text-[10px] uppercase tracking-wide text-toga font-bold">{p.category} · {p.subcategory}</span>
                  <p className="font-display uppercase font-bold text-charcoal">{p.name}</p>
                  <p className="text-xs text-charcoal/50 mt-1">{p.status}</p>
                  <div className="flex gap-3 mt-3">
                    <button onClick={() => setEditing(p)} data-testid={`edit-product-${p._id}`} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-charcoal hover:text-toga"><Pencil size={14} /> Edit</button>
                    <button onClick={() => deleteProduct(p._id)} data-testid={`delete-product-${p._id}`} className="inline-flex items-center gap-1 text-xs uppercase font-bold text-charcoal/50 hover:text-toga"><Trash2 size={14} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-charcoal/80 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={saveProduct} data-testid="product-form" className="relative bg-cream w-full max-w-lg max-h-[90vh] overflow-y-auto border border-charcoal/20 p-8">
            <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-charcoal hover:text-toga"><X size={22} /></button>
            <h3 className="font-display uppercase font-bold text-charcoal text-2xl mb-6">{editing._id ? "Edit" : "Add"} Product</h3>
            <div className="space-y-5">
              <input required className="line-input" placeholder="Name *" value={editing.name} onChange={set("name")} data-testid="pf-name" />
              <div className="grid grid-cols-2 gap-4">
                <select className="line-input" value={editing.category} onChange={set("category")} data-testid="pf-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input className="line-input" placeholder="Subcategory" value={editing.subcategory} onChange={set("subcategory")} data-testid="pf-subcategory" />
              </div>
              <input required className="line-input" placeholder="Image URL *" value={editing.image} onChange={set("image")} data-testid="pf-image" />
              <select className="line-input" value={editing.status} onChange={set("status")} data-testid="pf-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea rows={2} className="line-input resize-none" placeholder="Description" value={editing.description} onChange={set("description")} data-testid="pf-description" />
              <textarea rows={2} className="line-input resize-none" placeholder="Details (weave / styling notes)" value={editing.details} onChange={set("details")} data-testid="pf-details" />
              <label className="flex items-center gap-3 text-sm text-charcoal cursor-pointer">
                <input type="checkbox" checked={editing.featured} onChange={set("featured")} data-testid="pf-featured" /> Featured
              </label>
            </div>
            <button type="submit" data-testid="pf-save" className="mt-8 w-full bg-toga text-white px-8 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-charcoal transition-colors">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
